<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { ArrowRight, BarChart2, CheckCircle, FileText, Lock, Star, Upload, Zap, AlertCircle } from 'lucide-vue-next';
import { analyzeCV } from '../api/analysis';
import authService from '../api/auth';
import type { AnalysisResult } from '../api/analysis';
import { showSystemNotification } from '../utils/notifications';
import { API_BASE_URL } from '../api/baseUrl';

const isAnalyzing = ref(false);
const showResults = ref(false);
const score = ref(35);
const cvFile = ref<File | null>(null);
const jobDescription = ref('Senior Project Manager needed. Must have experience with Agile methodologies, Jira, and leading cross-functional teams in Fintech sector...');
const DEFAULT_FULL_PROMPT = `Jesteś ekspertem od analizy CV i dopasowywania ich do ofert pracy.

KRYTYCZNE ZASADY:
1. Nie dodawaj informacji, których nie ma w CV.
2. Jeśli wymaganie nie występuje w CV, oznacz je jako brak.
3. Odpowiadaj wyłącznie w JSON.

CV:
{{cvText}}

OFERTA:
{{jobPosting}}

Zwróć JSON w formacie:
{
  "matchScore": number,
  "highlightedStrengths": string[],
  "missingRequirements": string[],
  "changesMade": string[],
  "optimizedSections": {
    "experience": string,
    "skills": string,
    "summary": string
  },
  "warnings": string[]
}`;
const customPrompt = ref(DEFAULT_FULL_PROMPT);
const analysisResult = ref<AnalysisResult | null>(null);
const errorMessage = ref('');
const copyLabel = ref('Skopiuj');
const notifyOnAnalysisComplete = ref(false);

const optimizations = [
  {
    icon: CheckCircle,
    title: 'Słowa kluczowe dodane',
    desc: 'Agile, Jira, Stakeholder Management',
    tone: 'bg-emerald-50 border-emerald-100 text-emerald-800',
    iconClass: 'text-emerald-600'
  },
  {
    icon: Star,
    title: 'Podbito doświadczenie',
    desc: 'Wyróżniono projekt Fintech z 2021 roku',
    tone: 'bg-indigo-50 border-indigo-100 text-indigo-800',
    iconClass: 'text-indigo-600'
  },
  {
    icon: Lock,
    title: 'Anti-Hallucination Check',
    desc: 'Weryfikacja zgodności z prawdą: 100%',
    tone: 'bg-slate-50 border-slate-100 text-slate-700',
    iconClass: 'text-slate-500'
  }
];

let analysisDelay: number | undefined;
let scoreTimer: number | undefined;
let copyResetTimer: number | undefined;

const showAnalysisNotification = async (title: string, body: string) => {
  if (!notifyOnAnalysisComplete.value) return;
  if (document.visibilityState === 'visible') return;

  await showSystemNotification(title, {
    body,
    tag: 'cv-tailor-analysis-result'
  });
};

const clearTimers = () => {
  if (analysisDelay) {
    window.clearTimeout(analysisDelay);
    analysisDelay = undefined;
  }
  if (scoreTimer) {
    window.clearInterval(scoreTimer);
    scoreTimer = undefined;
  }
};

const clearCopyTimer = () => {
  if (copyResetTimer) {
    window.clearTimeout(copyResetTimer);
    copyResetTimer = undefined;
  }
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    cvFile.value = target.files[0];
    errorMessage.value = '';
  }
};

const getDownloadFileName = () => {
  const original = analysisResult.value?.originalFileName;
  if (!original) return 'CV-optimized.txt';
  const base = original.replace(/\.[^/.]+$/, '');
  return `${base}-optimized.txt`;
};

const copyOptimizedCV = async () => {
  if (!analysisResult.value?.optimizedCV) return;
  clearCopyTimer();
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(analysisResult.value.optimizedCV);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = analysisResult.value.optimizedCV;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    copyLabel.value = 'Skopiowano';
  } catch (err) {
    console.error('Copy error:', err);
    copyLabel.value = 'Błąd kopiowania';
  }
  copyResetTimer = window.setTimeout(() => {
    copyLabel.value = 'Skopiuj';
    clearCopyTimer();
  }, 1600);
};

const downloadOptimizedCV = () => {
  if (!analysisResult.value?.optimizedCV) return;
  const blob = new Blob([analysisResult.value.optimizedCV], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = getDownloadFileName();
  link.click();
  URL.revokeObjectURL(url);
};

const loadLastPrompt = async () => {
  if (!authService.isAuthenticated()) {
    customPrompt.value = DEFAULT_FULL_PROMPT;
    notifyOnAnalysisComplete.value = false;
    return;
  }

  try {
    const token = authService.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      customPrompt.value = DEFAULT_FULL_PROMPT;
      notifyOnAnalysisComplete.value = false;
      return;
    }

    const data = await response.json();
    const lastPrompt = data?.user?.lastPrompt;
    notifyOnAnalysisComplete.value = Boolean(data?.user?.notifyOnAnalysisComplete);
    customPrompt.value = typeof lastPrompt === 'string' && lastPrompt.trim().length > 0
      ? lastPrompt
      : DEFAULT_FULL_PROMPT;
  } catch (error) {
    console.error('Error loading prompt:', error);
    customPrompt.value = DEFAULT_FULL_PROMPT;
    notifyOnAnalysisComplete.value = false;
  }
};

const handleNotificationPreferenceChanged = (event: Event) => {
  const customEvent = event as CustomEvent<{ enabled?: boolean }>;
  notifyOnAnalysisComplete.value = Boolean(customEvent.detail?.enabled);
};

const startAnalysis = async () => {
  if (isAnalyzing.value) return;
  
  // Sprawdzenie autentykacji
  if (!authService.isAuthenticated()) {
    errorMessage.value = 'Musisz się zalogować aby analizować CV. Kliknij "Zaloguj się" w górnym rogu.';
    return;
  }
  
  if (!cvFile.value) {
    errorMessage.value = 'Proszę wybrać plik CV';
    return;
  }
  if (!jobDescription.value.trim()) {
    errorMessage.value = 'Proszę wkleić treść oferty pracy';
    return;
  }
  if (!customPrompt.value.trim()) {
    errorMessage.value = 'Proszę wpisać pełny prompt AI';
    return;
  }

  clearTimers();
  isAnalyzing.value = true;
  showResults.value = false;
  score.value = 35;
  errorMessage.value = '';
  analysisResult.value = null;

  try {
    // Wywołanie prawdziwego API
    const result = await analyzeCV(cvFile.value, jobDescription.value, customPrompt.value);
    analysisResult.value = result;

    // Emituj event że analiza się udała - Dashboard powinien się odświeżyć
    window.dispatchEvent(new Event('analysisCompleted'));

    void showAnalysisNotification(
      'Analiza CV zakończona',
      `Twój wynik jest gotowy (${result.matchScore}%). Kliknij, aby wrócić do szczegółów.`
    );

    // Animacja wyniku
    isAnalyzing.value = false;
    showResults.value = true;

    const end = result.matchScore;
    scoreTimer = window.setInterval(() => {
      if (score.value >= end) {
        clearTimers();
        return;
      }
      score.value += 1;
    }, 22);
  } catch (error) {
    isAnalyzing.value = false;
    errorMessage.value = error instanceof Error ? error.message : 'Wystąpił błąd podczas analizy';
    void showAnalysisNotification(
      'Analiza CV nie powiodła się',
      'Wystąpił błąd podczas analizy. Kliknij, aby spróbować ponownie.'
    );
    console.error('Analysis error:', error);
  }
};

onMounted(() => {
  loadLastPrompt();
  window.addEventListener('notificationPreferenceChanged', handleNotificationPreferenceChanged);
});

onBeforeUnmount(() => {
  clearTimers();
  clearCopyTimer();
  window.removeEventListener('notificationPreferenceChanged', handleNotificationPreferenceChanged);
});
</script>

<template>
  <section class="relative overflow-hidden py-20 pb-32 bg-gradient-to-b from-slate-50 via-white to-slate-50">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div id="demo" class="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden transform transition-all hover:scale-[1.01] duration-500">
    <div class="grid md:grid-cols-2">
      <div class="p-8 border-b md:border-b-0 md:border-r border-slate-100 bg-slate-50/50">
        <h3 class="font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Upload class="h-5 w-5 text-indigo-500" />
          1. Wgraj CV i ofertę
        </h3>
        <div class="space-y-4">
          <div class="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center bg-white hover:border-indigo-400 transition cursor-pointer group relative">
            <input
              type="file"
              accept=".pdf,.docx,.doc,.txt"
              @change="handleFileChange"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <FileText class="h-10 w-10 text-slate-400 mx-auto mb-2 group-hover:text-indigo-500 transition" />
            <p class="text-sm text-slate-500 font-medium">
              {{ cvFile ? cvFile.name : 'Kliknij, aby wybrać plik CV' }}
            </p>
            <p class="text-xs text-slate-400 mt-1">PDF, DOCX, DOC lub TXT (max 10MB)</p>
          </div>
          <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Treść ogłoszenia</p>
            <textarea
              v-model="jobDescription"
              class="w-full h-24 bg-slate-100 rounded-lg p-3 text-xs text-slate-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Wklej tutaj treść oferty pracy..."
            ></textarea>
          </div>
          <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Pełny prompt AI (wymagany)</p>
            <textarea
              v-model="customPrompt"
              class="w-full h-44 bg-slate-100 rounded-lg p-3 text-xs text-slate-700 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Wklej cały prompt. Użyj {{cvText}} oraz {{jobPosting}} jako placeholderów danych wejściowych."
            ></textarea>
          </div>
          <button
            type="button"
            :disabled="isAnalyzing"
            @click="startAnalysis"
            class="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition shadow-xl shadow-indigo-600/30 disabled:opacity-70 disabled:cursor-wait flex justify-center items-center gap-2"
          >
            <ArrowRight class="h-4 w-4" />
            {{ isAnalyzing ? 'Analizowanie...' : 'Analizuj dopasowanie' }}
          </button>
          <div v-if="errorMessage" class="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
            <AlertCircle class="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <p class="text-sm text-red-800">{{ errorMessage }}</p>
          </div>
        </div>
      </div>

      <div class="p-8 relative bg-white min-h-96 flex items-center justify-center">
        <div
          v-if="isAnalyzing"
          class="flex flex-col items-center justify-center text-slate-400"
        >
          <div class="h-[82px] w-16 mb-4 flex items-end justify-center gap-2.5">
            <div class="w-1.5 bg-indigo-500 rounded-t-lg animate-bar-1"></div>
            <div class="w-1.5 bg-indigo-500 rounded-t-lg animate-bar-2"></div>
            <div class="w-1.5 bg-indigo-500 rounded-t-lg animate-bar-3"></div>
          </div>
          <p class="text-slate-600">Trwa analiza CV...</p>
        </div>
        <div
          v-else-if="!showResults"
          class="flex flex-col items-center justify-center text-slate-400"
        >
          <BarChart2 class="h-16 w-16 mb-4 opacity-50" />
          <p>Uruchom analizę, aby zobaczyć wynik</p>
        </div>

        <div v-if="showResults" class="w-full">
          <h3 class="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <CheckCircle class="h-5 w-5 text-emerald-500" />
            2. Wynik i optymalizacja
          </h3>

          <div class="flex items-center justify-between mb-8">
            <div class="text-center w-1/2 border-r border-slate-100">
              <p class="text-sm text-slate-500 mb-1">Obecny wynik</p>
              <p class="text-4xl font-extrabold text-slate-300">35%</p>
            </div>
            <div class="text-center w-1/2">
              <p class="text-sm text-indigo-600 font-bold mb-1">Po optymalizacji</p>
              <p
                class="text-5xl font-extrabold transition-all duration-500"
                :class="showResults ? 'text-indigo-600 scale-110' : 'text-slate-300'"
              >
                {{ score }}%
              </p>
            </div>
          </div>

          <div class="space-y-3 animate-fade-in">
            <div
              v-for="(item, idx) in (analysisResult?.changesMade || []).slice(0, 3)"
              :key="idx"
              class="flex items-start gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-100"
            >
              <CheckCircle class="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
              <div>
                <p class="text-sm font-bold text-emerald-800">{{ item }}</p>
              </div>
            </div>
            <div
              v-for="(item, idx) in (analysisResult?.warnings || []).slice(0, 2)"
              :key="idx"
              class="flex items-start gap-3 p-3 rounded-lg border bg-amber-50 border-amber-100"
            >
              <AlertCircle class="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p class="text-sm font-bold text-amber-800">{{ item }}</p>
              </div>
            </div>
          </div>

          <div v-if="analysisResult?.optimizedCV" class="mt-4 p-4 border border-slate-200 rounded-xl bg-slate-50">
            <div class="flex items-center justify-between gap-3 mb-3">
              <div>
                <p class="text-xs font-bold text-slate-400 uppercase tracking-wide">Podgląd CV po optymalizacji</p>
                <p class="text-sm text-slate-600">Sprawdź treść przed skopiowaniem lub pobraniem</p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  @click="copyOptimizedCV"
                  class="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:border-indigo-300 hover:text-indigo-700 transition"
                >
                  {{ copyLabel }}
                </button>
                <button
                  type="button"
                  @click="downloadOptimizedCV"
                  class="px-3 py-2 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition"
                >
                  Pobierz TXT
                </button>
              </div>
            </div>
            <div class="h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
              {{ analysisResult.optimizedCV }}
            </div>
          </div>
        </div>

      </div>
    </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes barAnimation1 {
  0%, 100% { height: 10px; }
  50% { height: 40px; }
}

@keyframes barAnimation2 {
  0%, 100% { height: 20px; }
  50% { height: 50px; }
}

@keyframes barAnimation3 {
  0%, 100% { height: 15px; }
  50% { height: 35px; }
}

@keyframes barAnimation4 {
  0%, 100% { height: 25px; }
  50% { height: 45px; }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

.animate-bar-1 {
  animation: barAnimation1 1s ease-in-out infinite;
}

.animate-bar-2 {
  animation: barAnimation2 1s ease-in-out infinite 0.15s;
}

.animate-bar-3 {
  animation: barAnimation3 1s ease-in-out infinite 0.3s;
}
</style>
